import React, { useState, useRef } from 'react';
import { Box, Typography, TextField, IconButton, CircularProgress, Button } from '@mui/material';
import { MdContentCopy, MdCheck, MdError, MdCloudUpload, MdClose } from 'react-icons/md';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

let nextId = 0;

const ImageUploader = ({ onUploaded }) => {
    const [items, setItems] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);

    const uploadOne = async (item) => {
        try {
            const formData = new FormData();
            formData.append('image', item.file);

            const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/uploads`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const contentType = response.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error(`Server error (${response.status})`);
            }
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || 'Upload failed');
            }

            setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, status: 'done', url: result.url } : i));
            if (onUploaded) onUploaded(result.url);
        } catch (err) {
            console.error('Error uploading image:', err);
            setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, status: 'error', error: err.message } : i));
        }
    };

    const addFiles = (fileList) => {
        const newItems = Array.from(fileList)
            .filter((file) => ALLOWED_TYPES.includes(file.type))
            .map((file) => ({
                id: nextId++,
                file,
                preview: URL.createObjectURL(file),
                status: 'uploading',
                url: null,
                error: null
            }));

        if (newItems.length === 0) return;
        setItems((prev) => [...prev, ...newItems]);
        newItems.forEach(uploadOne);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        addFiles(e.dataTransfer.files);
    };

    const handleCopy = (url) => {
        navigator.clipboard.writeText(url);
        setItems((prev) => prev.map((i) => i.url === url ? { ...i, copied: true } : i));
        setTimeout(() => {
            setItems((prev) => prev.map((i) => i.url === url ? { ...i, copied: false } : i));
        }, 2000);
    };

    const removeItem = (id) => {
        setItems((prev) => prev.filter((i) => i.id !== id));
    };

    return (
        <Box>
            <Box
                onClick={() => inputRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                sx={{
                    border: dragActive ? '2px dashed #f59e0b' : '2px dashed rgba(120, 53, 15, 0.6)',
                    borderRadius: '8px',
                    backgroundColor: dragActive ? 'rgba(245, 158, 11, 0.1)' : 'rgba(20, 15, 10, 0.4)',
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                }}
            >
                <MdCloudUpload size={32} color="#f59e0b" />
                <Typography sx={{ color: 'rgba(254, 243, 199, 0.8)', mt: 1 }}>
                    Drag &amp; drop images here, or click to browse
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(254, 243, 199, 0.5)' }}>
                    JPG, PNG, GIF, WEBP — up to 8MB each — multiple files supported
                </Typography>
                <input
                    ref={inputRef}
                    type="file"
                    accept={ALLOWED_TYPES.join(',')}
                    multiple
                    hidden
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                />
            </Box>

            {items.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {items.map((item) => (
                        <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(20, 15, 10, 0.5)', borderRadius: '8px', p: 1 }}>
                            <Box component="img" src={item.preview} sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '4px' }} />

                            {item.status === 'uploading' && (
                                <>
                                    <CircularProgress size={18} sx={{ color: '#f59e0b' }} />
                                    <Typography variant="body2" sx={{ color: 'rgba(254, 243, 199, 0.7)', flexGrow: 1 }}>{item.file.name}</Typography>
                                </>
                            )}

                            {item.status === 'error' && (
                                <>
                                    <MdError color="#f87171" />
                                    <Typography variant="body2" sx={{ color: '#f87171', flexGrow: 1 }}>{item.error}</Typography>
                                    <Button size="small" onClick={() => { setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, status: 'uploading', error: null } : i)); uploadOne(item); }}>
                                        Retry
                                    </Button>
                                </>
                            )}

                            {item.status === 'done' && (
                                <TextField
                                    value={item.url}
                                    InputProps={{ readOnly: true }}
                                    size="small"
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': { color: '#ffffff' },
                                        '& fieldset': { borderColor: 'rgba(245, 158, 11, 0.5)' }
                                    }}
                                />
                            )}
                            {item.status === 'done' && (
                                <IconButton onClick={() => handleCopy(item.url)} sx={{ color: '#f59e0b' }}>
                                    {item.copied ? <MdCheck /> : <MdContentCopy />}
                                </IconButton>
                            )}
                            <IconButton onClick={() => removeItem(item.id)} sx={{ color: 'rgba(254, 243, 199, 0.6)' }}>
                                <MdClose />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default ImageUploader;
